import type { ApiFailure } from '../auth/api-client';
import type { ChecklistSourceDiffCounts, ChecklistSourcePreview } from './checklists-api';

export interface ChecklistPreviewProduct {
  file: string;
  sourceSystem: string;
  sourceTargetId: string;
  product: ChecklistSourcePreview['product'];
  fingerprint: string;
  previewedAt: string;
  mode: 'ORDINARY';
  verification: 'NOT_RUN';
  sourceMissingDisposition: 'RETAINED';
  roster: ChecklistSourceDiffCounts | null;
  parallels: ChecklistSourceDiffCounts | null;
  subsets: ChecklistSourceDiffCounts | null;
}
export interface ChecklistPreviewFailure {
  file: string;
  reason: string;
}
export interface ChecklistPreviewReport {
  schemaVersion: 1;
  reportType: 'CATALOG_IMPORT_PREVIEW';
  generatedAt: string;
  complete: boolean;
  products: ChecklistPreviewProduct[];
  failures: ChecklistPreviewFailure[];
}

/** Copies only public preview metadata. Submission keys and replacement tokens never enter reports. */
export function checklistPreviewProduct(
  file: string,
  preview: ChecklistSourcePreview,
  previewedAt = new Date().toISOString(),
): ChecklistPreviewProduct {
  if (preview.replaceExisting) throw new Error('Bulk preview requires ordinary import mode.');
  return {
    file,
    sourceSystem: preview.sourceSystem,
    sourceTargetId: preview.sourceTargetId,
    product: {
      category: preview.product.category,
      year: preview.product.year,
      brand: preview.product.brand,
      product: preview.product.product,
    },
    fingerprint: preview.fingerprint,
    previewedAt,
    mode: 'ORDINARY',
    verification: 'NOT_RUN',
    sourceMissingDisposition: 'RETAINED',
    roster: copyCounts(preview.diff),
    parallels: copyCounts(preview.parallelDiff),
    subsets: copyCounts(preview.subsetDiff),
  };
}
function copyCounts(
  value: ChecklistSourceDiffCounts | undefined,
): ChecklistSourceDiffCounts | null {
  if (
    !value ||
    ![value.adds, value.updates, value.unchanged, value.sourceMissing].every(
      (count) => Number.isSafeInteger(count) && count >= 0,
    )
  )
    return null;
  return {
    adds: value.adds,
    updates: value.updates,
    unchanged: value.unchanged,
    sourceMissing: value.sourceMissing,
  };
}
export function checklistPreviewReport(
  products: ChecklistPreviewProduct[],
  failures: ChecklistPreviewFailure[],
  complete: boolean,
): ChecklistPreviewReport {
  return {
    schemaVersion: 1,
    reportType: 'CATALOG_IMPORT_PREVIEW',
    generatedAt: new Date().toISOString(),
    complete,
    products,
    failures,
  };
}
export function downloadChecklistPreview(report: ChecklistPreviewReport, filename: string): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
function readable(value: unknown, limit = 512): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  return value
    .slice(0, limit)
    .replace(/https?:\/\/\S+|s3:\/\/\S+/gi, '[URL omitted]')
    .split('')
    .map((character) =>
      character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127 ? ' ' : character,
    )
    .join('');
}
/** Render a bounded allowlist, never JSON-stringify a response or raw source input. */
export function checklistFailureReason(failure: ApiFailure): string {
  const isValidation = ['source_snapshot_invalid', 'invalid_source_snapshot'].some(
    (message) => failure.message === message || failure.error === message,
  );
  const body = record(failure.body);
  const trustedEnvelope =
    isValidation &&
    Number.isSafeInteger(body?.totalErrorCount) &&
    (body!.totalErrorCount as number) >= 0 &&
    typeof body?.truncated === 'boolean';
  const errors = trustedEnvelope && Array.isArray(body?.errors) ? body.errors.slice(0, 50) : [];
  const details = errors.flatMap((value) => {
    const error = record(value);
    if (!error) return [];
    const code =
      typeof error.code === 'string' && /^[a-z][a-z0-9-]{0,95}$/.test(error.code)
        ? error.code
        : undefined;
    if (!code) return [];
    // The current source-validation envelope supplies fixed server-sanitized reasons.
    const reason = readable(error.detail);
    const context: string[] = [];
    if (
      typeof error.fieldPath === 'string' &&
      error.fieldPath.length <= 256 &&
      /^\/(?:product|rows|parallelDefinitions|subsetDefinitions|schemaVersion|extractedAt|sourceHighWater|expectedRosterCount|expectedParallelDefinitionCount|expectedSubsetDefinitionCount)(?:\/[A-Za-z][A-Za-z0-9]*|\/\d+)*$/.test(
        error.fieldPath,
      )
    )
      context.push(`fieldPath: ${error.fieldPath}`);
    for (const key of ['sourceTargetId', 'sourceItemId', 'sourceSubsetId']) {
      const value = error[key];
      if (typeof value === 'string' && /^[0-9]{1,64}$/.test(value))
        context.push(`${key}: ${value}`);
    }
    if (
      typeof error.definitionId === 'string' &&
      /^azure-sql-ebay#[0-9]{1,64}#[0-9]{1,64}$/.test(error.definitionId)
    )
      context.push(`definitionId: ${error.definitionId}`);
    return [[code, reason, ...context].filter(Boolean).join(' · ')];
  });
  const summary = isValidation
    ? 'Source snapshot is invalid.'
    : `Request failed${Number.isInteger(failure.status) && failure.status >= 100 && failure.status <= 599 ? ` (HTTP ${failure.status})` : ''}. Try previewing again.`;
  const total = trustedEnvelope ? body?.totalErrorCount : undefined;
  const suffix =
    trustedEnvelope &&
    (body?.truncated === true || (typeof total === 'number' && total > details.length))
      ? ` Showing ${details.length}${Number.isSafeInteger(total) && (total as number) >= details.length ? ` of ${total}` : ''} validation errors; more errors were omitted.`
      : '';
  return [summary, ...details].join('\n').slice(0, 16384) + suffix;
}
